import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';

import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';

import {
  isDateField,
  isLookupSelectField,
  isMultiSelectField,
  isSelectField,
  isTextField,
} from '@/components/Glossary/Sections/Approvals/TemplateForm/MetadataCondition.utils';
import { operationOptions } from '@/components/Glossary/Sections/Approvals/TemplateForm/TemplateForm.utils';
import { SelectLookup } from '@/components/Metadata/Fieldtypes/Latest/SelectLookup';
import DatePickerNative from '@/components/UI/DatePickerNative';
import ReactSelect from '@/components/UI/Select';

interface MetadataConditionProps {
  studyMetadataOptions: Array<MetadataField>;
  projectMetadataOptions: Array<MetadataField>;
  glossary_id?: ID;
  operation?: string;
  value?: MetadataFieldValueTypes;
  index: number;
}

interface OptionSelectItem {
  value: ID;
  label: string;
}

interface SelectValueOption {
  value: string;
  label: string;
}

const defaultSelectValue: SelectValueOption = { label: 'Select...', value: '' };
const availableMetadataFieldTypes: ReadonlyArray<string> = [
  'text',
  'select',
  'lookup_select',
  'lookup_multi_select',
  'date',
];

const buildSelectStyles = (hasErrors: boolean) => ({
  control: (base: Record<string, string | number | undefined>) => ({
    ...base,
    border: hasErrors ? '2px solid red !important' : base.border,
    height: 40,
    minHeight: 40,
    '&:hover': {
      border: hasErrors ? '2px solid red !important' : base.border,
    },
  }),
});

const toMetadataOptions = (fields: Array<MetadataField>): Array<OptionSelectItem> =>
  fields.reduce<Array<OptionSelectItem>>((acc, { field_type, id, title }) => {
    if (availableMetadataFieldTypes.includes(field_type)) {
      acc.push({ value: id, label: title });
    }
    return acc;
  }, []);

export const MetadataCondition: FC<MetadataConditionProps> = ({
  studyMetadataOptions,
  projectMetadataOptions,
  glossary_id: defaultGlossaryId,
  operation: defaultOperation,
  value: defaultValue,
  index,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const glossaryIdFieldName = `conditions.${index}.glossary_id` as const;
  const operationFieldName = `conditions.${index}.operation` as const;
  const valueFieldName = `conditions.${index}.value` as const;

  const metadata = useMemo(
    () => [...(studyMetadataOptions ?? []), ...(projectMetadataOptions ?? [])],
    [studyMetadataOptions, projectMetadataOptions]
  );

  const { groupedMetadataOptions, flatMetadataOptions } = useMemo(() => {
    const grouped: Array<{ label: string; options: Array<OptionSelectItem> }> = [];
    const flat: Array<OptionSelectItem> = [];
    if (_isNotEmpty(studyMetadataOptions)) {
      const options = toMetadataOptions(studyMetadataOptions);
      grouped.push({ label: 'Study metadata', options });
      flat.push(...options);
    }
    if (_isNotEmpty(projectMetadataOptions)) {
      const options = toMetadataOptions(projectMetadataOptions);
      grouped.push({ label: 'Project metadata', options });
      flat.push(...options);
    }
    return { groupedMetadataOptions: grouped, flatMetadataOptions: flat };
  }, [studyMetadataOptions, projectMetadataOptions]);

  const glossaryId = useWatch({
    control,
    name: glossaryIdFieldName,
    defaultValue: defaultGlossaryId ?? null,
  }) as ID | null | undefined;

  const operation = useWatch({
    control,
    name: operationFieldName,
    defaultValue: defaultOperation ?? '',
  }) as string;

  if (!_isNotEmpty(studyMetadataOptions) && !_isNotEmpty(projectMetadataOptions)) {
    return null;
  }

  const conditionErrors = (errors?.conditions as Array<Record<string, unknown>> | undefined)?.[index];
  const hasGlossaryIdError = _notNil(conditionErrors?.glossary_id);
  const hasOperationError = _notNil(conditionErrors?.operation);
  const hasValueError = _notNil(conditionErrors?.value);

  const getSelectValueOptions = (id: ID | null | undefined): Array<SelectValueOption> => {
    const item = metadata.find((m) => m.id === id);
    if (item?.field_type === 'select' || item?.field_type === 'multi_select') {
      return (item.options ?? []).map((value: string) => ({
        label: safelyDecodeURIComponent(value),
        value,
      }));
    }
    return [];
  };

  const handleSelectChange = (
    onChange: (value: Array<MetadataFieldValueTypes> | MetadataFieldValueTypes) => void,
    data: SelectValueOption | Array<SelectValueOption> | null | undefined,
    isMulti: boolean
  ) => {
    if (_notNil(data)) {
      if (!Array.isArray(data)) {
        onChange(data.value);
      } else if (isMulti) {
        onChange(data?.map((d) => d.value) ?? undefined);
      }
    } else {
      onChange('');
    }
  };

  const getDefaultSelectValue = (
    value: Array<MetadataFieldValueTypes> | MetadataFieldValueTypes,
    isMulti: boolean
  ): SelectValueOption | Array<SelectValueOption> | undefined => {
    if (_notNil(value) && value !== '') {
      const valueOptions = getSelectValueOptions(glossaryId);
      if (isMulti && Array.isArray(value)) {
        return valueOptions.filter((option) => value.includes(option.value));
      }
      return valueOptions.find((option) => option.value === value);
    }
    return isMulti ? [] : defaultSelectValue;
  };

  const metadataItem = metadata.find(({ id }) => id === glossaryId);

  return (
    <div
      className="flex flex-row justify-between items-start"
      data-test-component="MetadataCondition"
      data-test-element="container"
      data-test-key={`${index}`}
      data-testid="metadata-condition"
    >
      <div
        className="flex flex-column"
        data-test-element="metadata-field-select"
        data-testid="metadata-glossary-item-field"
      >
        <Controller
          defaultValue={defaultGlossaryId ?? null}
          name={glossaryIdFieldName}
          rules={{ required: 'This field is required' }}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ReactSelect
              customStyles={buildSelectStyles(hasGlossaryIdError)}
              isMulti={false}
              className="mr3"
              defaultValue={
                _notNil(value) && value !== ''
                  ? flatMetadataOptions.find((option) => option.value === value)
                  : defaultSelectValue
              }
              options={groupedMetadataOptions}
              onChange={(option) => onChange((option as OptionSelectItem | null)?.value ?? '')}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={glossaryIdFieldName}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>

      <div className="flex flex-column" data-test-element="operation-select">
        <Controller
          defaultValue={defaultOperation ?? ''}
          name={operationFieldName}
          rules={{ required: 'This field is required' }}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ReactSelect
              customStyles={buildSelectStyles(hasOperationError)}
              isMulti={false}
              className="mr3"
              defaultValue={
                _notNil(value) && value !== ''
                  ? operationOptions.find((option) => option.value === value)
                  : defaultSelectValue
              }
              options={operationOptions}
              onChange={(option) => onChange((option as { value: string } | null)?.value ?? '')}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={operationFieldName}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>

      {_notNil(glossaryId) && _notNil(operation) && operation !== '' && (
        <ValueField
          glossaryId={glossaryId}
          metadata={metadata}
          metadataItem={metadataItem}
          index={index}
          valueFieldName={valueFieldName}
          defaultValue={defaultValue}
          hasValueError={hasValueError}
          getSelectValueOptions={getSelectValueOptions}
          getDefaultSelectValue={getDefaultSelectValue}
          handleSelectChange={handleSelectChange}
        />
      )}
    </div>
  );
};

interface ValueFieldProps {
  glossaryId: ID;
  metadata: Array<MetadataField>;
  metadataItem: MetadataField | undefined;
  index: number;
  valueFieldName: string;
  defaultValue: MetadataFieldValueTypes | undefined;
  hasValueError: boolean;
  getSelectValueOptions: (id: ID | null | undefined) => Array<SelectValueOption>;
  getDefaultSelectValue: (
    value: Array<MetadataFieldValueTypes> | MetadataFieldValueTypes,
    isMulti: boolean
  ) => SelectValueOption | Array<SelectValueOption> | undefined;
  handleSelectChange: (
    onChange: (value: Array<MetadataFieldValueTypes> | MetadataFieldValueTypes) => void,
    data: SelectValueOption | Array<SelectValueOption> | null | undefined,
    isMulti: boolean
  ) => void;
}

const ValueField: FC<ValueFieldProps> = ({
  glossaryId,
  metadata,
  metadataItem,
  index,
  valueFieldName,
  defaultValue,
  hasValueError,
  getSelectValueOptions,
  getDefaultSelectValue,
  handleSelectChange,
}) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  if (isTextField(glossaryId, metadata)) {
    return (
      <div className="flex flex-column w5 mr3" data-test-element="metadata-text-value">
        <input
          type="text"
          data-test-element="metadata-text-value-input"
          data-testid={`metadata-text-input-${index}`}
          style={{ marginBottom: 0 }}
          className={cn({ input__error: hasValueError })}
          defaultValue={(defaultValue as string) ?? ''}
          {...register(valueFieldName, { required: 'This field is required' })}
        />
        <ErrorMessage
          errors={errors}
          name={valueFieldName}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
    );
  }
  if (isSelectField(glossaryId, metadata) || isMultiSelectField(glossaryId, metadata)) {
    const isMulti = isMultiSelectField(glossaryId, metadata);
    return (
      <div className="flex flex-column w5 mr3" data-test-element="metadata-select-value">
        <Controller
          defaultValue={defaultValue ?? (isMulti ? [] : '')}
          name={valueFieldName}
          rules={{ required: 'This field is required' }}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ReactSelect
              className="mr3"
              defaultValue={getDefaultSelectValue(
                value as Array<MetadataFieldValueTypes> | MetadataFieldValueTypes,
                isMulti
              )}
              options={getSelectValueOptions(glossaryId)}
              onChange={(data: unknown) => {
                handleSelectChange(
                  onChange,
                  data as SelectValueOption | Array<SelectValueOption> | null | undefined,
                  isMulti
                );
              }}
              isMulti={isMulti}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={valueFieldName}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
    );
  }
  if (isDateField(glossaryId, metadata)) {
    return (
      <div className="flex flex-column w5 mr3" data-test-element="metadata-date-value">
        <Controller
          defaultValue={(defaultValue as string) ?? ''}
          name={valueFieldName}
          control={control}
          rules={{ required: 'This field is required' }}
          render={({ field: { onChange, value } }) => (
            <DatePickerNative
              name="dateCollected"
              value={(value as string) ?? ''}
              onChange={onChange}
              style={{ marginBottom: 0 }}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={valueFieldName}
          render={({ message }) => <p className="f7 red db lh-copy pt1">{message}</p>}
        />
      </div>
    );
  }
  if (isLookupSelectField(glossaryId, metadata) && _notNil(metadataItem)) {
    return (
      <div className="flex flex-column w5 mr3" data-test-element="metadata-lookup-select-value">
        <SelectLookup
          field={{ ...metadataItem, value: defaultValue ?? '', required: true }}
          testId={valueFieldName}
          name={valueFieldName}
          isMulti={metadataItem.field_type === 'lookup_multi_select'}
        />
        <ErrorMessage
          errors={errors}
          name={valueFieldName}
          render={({ message }) => <p className="f6 red db lh-copy pt1">{message}</p>}
        />
      </div>
    );
  }
  return null;
};
